// How an OpenAI agent's owner and an OpenAI project's agent visibility should be labelled.
//
// Discovery places each OpenAI agent in the project OpenAI names for the key that listed it, and
// gives the agent that project's owner (evidence `project` + `ownerSource: "project"`). Each
// OpenAI project signal says whether its agents are visible to Axaris (`agentCoverage.status`).
// Signals written before that carry neither, and must render exactly as they always have.

const isLive = (s) => s && s.status !== "Gone";

const firstLiveOpenAiSignal = (signals, signalType) =>
  (signals || []).find(
    (s) => isLive(s) && s.signalType === signalType && s.sourceSystem === "OPENAI"
  ) || null;

/**
 * Where an OpenAI agent's shown owner came from, or null when there is nothing to say.
 *   null                                         no live OpenAI agent signal, or no `project` evidence
 *   { kind: "earlierScan" }                      the signal was not re-read this scan, or the agent's
 *                                                project owner could not be confirmed: any owner
 *                                                shown was kept from an earlier scan
 *   { kind: "project", projectId, projectName }  inherited from the agent's project this scan
 * Only meaningful when the asset actually shows an owner.
 */
export function ownerProvenance(signals) {
  const signal = firstLiveOpenAiSignal(signals, "OpenAiAgent");
  const evidence = signal && signal.evidence;
  const project = evidence && evidence.project;
  if (!project || typeof project !== "object") return null;
  // A preserved signal still carries its old ownerSource, but the owner was not read now.
  if (evidence.notReReadThisCycle === true) return { kind: "earlierScan" };
  if (project.attribution === "attributed" && evidence.ownerSource === "project") {
    const projectId = project.projectId ?? null;
    const projectName =
      typeof project.projectName === "string" && project.projectName !== ""
        ? project.projectName
        : projectId;
    return { kind: "project", projectId, projectName };
  }
  return { kind: "earlierScan" };
}

export const AGENT_COVERAGE_LABEL = {
  covered: "Agents visible: a project key is supplied",
  readFailed: "Agents not readable: the project key was refused or incomplete",
  notCovered: "Agents not visible: no project key supplied for this project",
  unknown: "Agent visibility unknown: a supplied key could not be placed in a project",
};

/**
 * The agent coverage of an OpenAI project, or null when it carries no recognised status (signals
 * written before per-project coverage have none, and render nothing).
 */
export function agentCoverage(signals) {
  const signal = firstLiveOpenAiSignal(signals, "AiProviderProject");
  const coverage = signal && signal.evidence && signal.evidence.agentCoverage;
  if (!coverage || typeof coverage !== "object") return null;
  return Object.prototype.hasOwnProperty.call(AGENT_COVERAGE_LABEL, coverage.status) ? coverage : null;
}
